import { useState, useCallback } from 'react';
import { useUpload } from './useUpload';
import api from '../utils/api';
import { logger } from '../utils/logger';

interface UseDeleteImageReturn {
  deleteImage: (imageId: string) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

/**
 * Hook for deleting a single image from the current catalog item.
 *
 * Calls DELETE /api/upload/{catalogId}/images/{imageId}, then removes
 * the image from local upload context state.
 *
 * If the deleted image was the selected cover, the cover is cleared so
 * the user can pick a new one.
 */
export function useDeleteImage(ownerId?: string | null): UseDeleteImageReturn {
  const { catalogId, uploadedImages, setUploadedImages, selectedCoverId, setSelectedCoverId } = useUpload();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteImage = useCallback(
    async (imageId: string): Promise<void> => {
      if (!catalogId) {
        setError('Cannot delete image: catalog not yet created');
        return;
      }

      setIsDeleting(true);
      setError(null);

      try {
        const base = `/api/upload/${catalogId}/images/${imageId}`;
        const url = ownerId ? `${base}?owner_id=${encodeURIComponent(ownerId)}` : base;
        await api.delete(url);

        // Remove from local state (immutable: create new array without deleted image)
        const updatedImages = (uploadedImages as Array<{ imageId: string }>).filter(
          (img) => img.imageId !== imageId
        );
        setUploadedImages(updatedImages);

        // If the deleted image was the cover, clear the cover selection
        if (selectedCoverId === imageId) {
          setSelectedCoverId(null);
        }
      } catch (err) {
        logger.error('Failed to delete image', { catalogId, imageId, err });
        const message = err instanceof Error ? err.message : 'Failed to delete image';
        setError(message);
      } finally {
        setIsDeleting(false);
      }
    },
    [catalogId, uploadedImages, setUploadedImages, selectedCoverId, setSelectedCoverId, ownerId]
  );

  return { deleteImage, isDeleting, error };
}
